import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

export const LEVEL_CONFIG = {
  domain: { color: 'purple', label: 'Domain' },
  topic: { color: 'primary', label: 'Topic' },
  subtopic: { color: 'skyblue', label: 'Subtopic' },
  specialty: { color: 'cyan', label: 'Specialty' },
  concept: { color: 'green', label: 'Concept' },
};

export const LEVELS = Object.keys(LEVEL_CONFIG);

export const SKILL_STATUSES = ['draft', 'published', 'deprecated'];
export const REVIEW_STATES = ['unreviewed', 'flagged', 'cleared'];
export const EDGE_KINDS = ['requires', 'related'];
export const EDGE_STATUSES = ['proposed', 'validated', 'demoted'];

export const CONTENT_TAG_SOURCE = 'human';
export const CURRICULUM_SCOPE_SOURCE = 'declared';

export const SKILL_SOURCE_LABELS = {
  human: 'human',
  section_map: 'section mapping',
  inherited: 'inherited',
  ai: 'AI',
  syllabus: 'syllabus',
  declared: 'declared',
};

// A deprecated node has been retired from the taxonomy, so it is not somewhere new
// content or curriculum should be attached. Draft stays available, since nothing is
// published yet and tagging cannot wait for that.
export const ASSOCIABLE_SKILL_STATUSES = SKILL_STATUSES.filter(status => status !== 'deprecated');

// A clone covers the same concepts but owns none of the rows, so they go across as new.
export const cloneSkillAssociations = rows =>
  rows && rows.filter(row => !row._destroy).map(({ mastery_skill_id }) => ({ mastery_skill_id }));

// `source` is left off: the server decides it, so sending it back would only invite the
// form to argue about what wrote a row.
const toNestedAttributes = rows =>
  rows.map(({ _destroy, id, mastery_skill_id }) => ({ _destroy, id, mastery_skill_id }));

// Spread into a params object. Nothing at all when the form never loaded the rows, so a
// payload that says nothing about them leaves them alone.
export const contentSkillTagParams = tags => tags && { content_skill_tags_attributes: toNestedAttributes(tags) };
export const curriculumSkillScopeParams = scopes =>
  scopes && { curriculum_skill_scopes_attributes: toNestedAttributes(scopes) };

export const joinAncestorPath = path => path.join(' › ');

export function useRequestedItems(requestStatus, storeKey) {
  const items = useSelector(state => state[storeKey].items);
  return useMemo(() => (requestStatus?.ids || []).map(id => items[id]).filter(Boolean), [requestStatus?.ids, items]);
}

// A refetch starts from a fresh request key, so the new status is empty for a render or
// two. Holding the last ids across that gap keeps the rows mounted, which is what stops
// a reload from collapsing everything the admin had expanded.
export function useStickyIds(requestStatus) {
  const lastIdsRef = useRef([]);
  if (requestStatus?.ids) lastIdsRef.current = requestStatus.ids;
  return requestStatus?.ids || lastIdsRef.current;
}
